import { Alert, Button, Modal, Skeleton, Typography } from "antd"
import { useState } from "react"
import useFeatureFlag from '../../../hooks/useFeatureFlag'
import { isRecoverySettingsForbidden, DUAL_PRICING_FEATURE, useRecoveryComponentQuery, useSetRecoveryComponentEnabledMutation } from "../../../domain/invoicing/settings/recoveryComponentQueries"
import SwitchWithDescription from "../../common/SwitchWithDescription"
import VSpace from "../../common/VSpace"
import { isTurnOnBlocked } from "../../paymentFees/paymentFeesModel"
import PaymentFeesBlockedAlert from "../../paymentFees/PaymentFeesBlockedAlert"
import PaymentFeesTable from "../../paymentFees/PaymentFeesTable"
import SurchargeDisclaimer, { SURCHARGE_DISCLAIMER_OK_TEXT } from "../../paymentFees/SurchargeDisclaimer"
import SettingsTitledCard from "../SettingsTitledCard"
import { shouldShowPassOnPaymentFeesSettings, toPassOnPaymentFeesView } from "./passOnPaymentFeesModel"

const TITLE = "Pass On Payment Fees"

const PassOnPaymentFeesCard = () => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const featureEnabled = useFeatureFlag(DUAL_PRICING_FEATURE)
  const settingsQuery = useRecoveryComponentQuery()
  const updateMutation = useSetRecoveryComponentEnabledMutation(setErrorMessage)

  if (!featureEnabled || (settingsQuery.isError && isRecoverySettingsForbidden(settingsQuery.error))) {
    return null
  }
  if (settingsQuery.isLoading) {
    return <SettingsTitledCard title={TITLE} description="Default for new jobs"><Skeleton active /></SettingsTitledCard>
  }
  if (settingsQuery.isError) {
    return <SettingsTitledCard title={TITLE} description="Default for new jobs"><Alert type="error"
      message="Unable to load the default for new jobs."
      action={<Button onClick={() => settingsQuery.refetch()}>Retry</Button>} /></SettingsTitledCard>
  }
  if (!shouldShowPassOnPaymentFeesSettings(settingsQuery.data)) {
    return null
  }

  const view = toPassOnPaymentFeesView(settingsQuery.data)

  const setEnabled = (enabled: boolean, acceptedSurchargeDisclaimer: boolean) => {
    setErrorMessage(null)
    updateMutation.mutate(
      { enabled, acceptedSurchargeDisclaimer },
      { onSuccess: () => setIsConfirmOpen(false) }
    )
  }

  const handleToggle = () => {
    if (view.isOn) {
      setEnabled(false, false)
    } else {
      setErrorMessage(null)
      setIsConfirmOpen(true)
    }
  }

  return <SettingsTitledCard
    title={TITLE}
    description="Choose whether new jobs add a surcharge when customers pay by credit card or bank transfer."
  >
    <VSpace size="middle">
      <SwitchWithDescription
        checked={view.isOn}
        disabled={isTurnOnBlocked(view, view.isOn) || updateMutation.isLoading || settingsQuery.isFetching}
        label="Pass credit card fees on to customers by default"
        description="Changing this default does not change existing jobs. Copied jobs, including bulk copies, keep the source job’s setting."
        onClick={handleToggle}
      />
      <PaymentFeesBlockedAlert reason={view.blockedReason} />
      {errorMessage && <Alert type="error" showIcon message={errorMessage} />}
      <PaymentFeesTable rows={view.rows} />
      {view.rows.length > 0 && <div>
        <Typography.Text type="secondary">
          Your processing cost is what SynkedUP Payments charges you. Prices don't change: the surcharge is added at
          checkout, worked out from the amount being paid, and never more than the maximum shown.
          Debit and prepaid cards are never surcharged.
        </Typography.Text>
        {view.ratesReadAtText && <>
          <br/>
          <Typography.Text type="secondary">
            Rates read from your SynkedUP Payments account on {view.ratesReadAtText}. You can't change them here.
          </Typography.Text>
        </>}
      </div>}
    </VSpace>
    <Modal
      open={isConfirmOpen}
      title="Turn on the payment surcharge for new jobs?"
      okText={SURCHARGE_DISCLAIMER_OK_TEXT}
      onOk={() => setEnabled(true, true)}
      confirmLoading={updateMutation.isLoading}
      onCancel={() => setIsConfirmOpen(false)}
    >
      <p>Prices on new jobs don't change. Customers pay a surcharge only when they pay electronically:</p>
      <ul>
        <li>Credit card: {view.cardMaximumText ?? 'not surcharged'}</li>
        <li>Bank transfer: {view.bankMaximumText ?? 'not surcharged'}</li>
      </ul>
      <p>Existing jobs aren't changed. Copied jobs keep the source job’s setting.</p>
      <SurchargeDisclaimer />
      {errorMessage && <Alert type="error" showIcon message={errorMessage} />}
    </Modal>
  </SettingsTitledCard>
}

export default PassOnPaymentFeesCard
